import posthog from 'posthog-js'
import type { User } from './types/api'

const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined
const host =
  (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ||
  'https://eu.i.posthog.com'

/** Coach / coached trainee / solo self-trainer */
export type UserMode = 'coach' | 'coached' | 'solo'

/** local | stage | prod (and aliases like "production" → prod) */
export function normalizeAnalyticsEnvironment(raw: string): string {
  if (raw === 'production') return 'prod'
  return raw
}

export function resolveAnalyticsEnvironment(): string {
  const raw =
    (import.meta.env.VITE_PUBLIC_POSTHOG_ENVIRONMENT as string | undefined) ||
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    'local'
  return normalizeAnalyticsEnvironment(raw)
}

const BASE_SUPER_PROPS = {
  app: 'tma',
  environment: resolveAnalyticsEnvironment(),
} as const

const USER_SUPER_PROP_KEYS = [
  'user_id',
  'username',
  'display_name',
  'first_name',
  'user_mode',
  'system_role',
  'plan_code',
  'has_subscription',
  'has_trainees',
  'has_coaches',
  'has_active_coach',
] as const

let initialized = false

export function isPostHogEnabled(): boolean {
  return Boolean(key) && initialized
}

export function initPostHog(): void {
  if (!key || initialized) {
    return
  }

  posthog.init(key, {
    api_host: host,
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    // SPA: capture route changes manually via PostHogPageview
    capture_pageview: false,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
  })
  posthog.register({ ...BASE_SUPER_PROPS })
  initialized = true
}

export function resolveUserMode(user: User): UserMode {
  if (user.hasTrainees) {
    return 'coach'
  }
  if (user.entitlements?.hasActiveCoach || user.hasCoaches) {
    return 'coached'
  }
  return 'solo'
}

/** Paid or trial plan (not free). */
export function hasActiveSubscription(user: User): boolean {
  const plan = user.planCode
  return plan === 'SOLO' || plan === 'PRO' || plan === 'TRIAL'
}

export function displayNameOf(user: User): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  if (full) return full
  if (user.username) return user.username
  return String(user.id)
}

/** Props attached to the person profile and registered on every later event. */
export function userAnalyticsProperties(user: User): Record<string, unknown> {
  return {
    user_id: user.id,
    username: user.username ?? null,
    display_name: displayNameOf(user),
    first_name: user.firstName ?? null,
    user_mode: resolveUserMode(user),
    system_role: user.role,
    plan_code: user.planCode ?? 'FREE',
    has_subscription: hasActiveSubscription(user),
    has_trainees: user.hasTrainees,
    has_coaches: user.hasCoaches,
    has_active_coach: Boolean(user.entitlements?.hasActiveCoach),
  }
}

/**
 * Identify the user and register super-properties so pageviews, autocapture,
 * and custom events all carry id / name / mode / subscription.
 */
export function syncAnalyticsUser(
  user: User,
  extra?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled()) {
    return
  }
  const props = { ...userAnalyticsProperties(user), ...extra }
  posthog.identify(String(user.id), props)
  posthog.register(props)
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!isPostHogEnabled()) {
    return
  }
  posthog.capture(event, properties)
}

/** Normalize `/v1/foo/123/bar` → `/v1/foo/:id/bar` for breakdowns. */
export function normalizeApiPath(endpoint: string): string {
  return endpoint
    .split('?')[0]
    .replace(/\/\d+/g, '/:id')
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '/:uuid',
    )
}

/** Broad coverage: successful mutating API calls across the TMA. */
export function captureApiMutation(
  method: string,
  endpoint: string,
): void {
  const upper = method.toUpperCase()
  if (upper === 'GET' || upper === 'HEAD' || upper === 'OPTIONS') {
    return
  }
  // Auth token exchange — skip noisy/sensitive path
  if (endpoint.includes('/auth/')) {
    return
  }
  captureEvent('api_mutation', {
    method: upper,
    path: normalizeApiPath(endpoint),
  })
}

export function resetPostHogUser(): void {
  if (!isPostHogEnabled()) {
    return
  }
  for (const prop of USER_SUPER_PROP_KEYS) {
    posthog.unregister(prop)
  }
  posthog.reset()
  posthog.register({ ...BASE_SUPER_PROPS })
}

export { posthog }
